import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Typography,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import TimerIcon from '@mui/icons-material/Timer';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { StepForm } from './StepForm';
import { STEP_TYPE_LABELS, inferStepType } from '../types/steps';
import { resolveNominalToken } from '../utils/resolveTemplateVars';
import type {
  ScriptStep,
  ScriptStepWithId,
  TestPointRef
} from '../types/steps';

type Props = {
  step: ScriptStepWithId;
  testPointRef?: TestPointRef | null;
  defaultExpanded?: boolean;
  onUpdate: (id: string, updates: Partial<ScriptStep>) => void;
  onDelete: (id: string) => void;
};

const STEP_ICONS: Record<string, React.ReactNode> = {
  command: <SendIcon fontSize="small" />,
  wait: <TimerIcon fontSize="small" />,
  measure: <CameraAltIcon fontSize="small" />,
  get_result_and_pic: <CameraAltIcon fontSize="small" />
};

const getStepSummary = (step: ScriptStepWithId): string => {
  const raw = step as unknown as Record<string, unknown>;
  switch (inferStepType(raw)) {
    case 'command':
      return (raw.action as string) || (raw.topic as string) || 'No action set';
    case 'wait': {
      const ms = raw.durationMs as number;
      return ms >= 1000 ? `${ms / 1000}s` : `${ms}ms`;
    }
    case 'measure':
    case 'get_result_and_pic':
      return raw.targetField === 'asLeft' ? 'As Left' : 'As Found';
    default:
      return '';
  }
};

export const StepCard = ({
  step,
  testPointRef,
  defaultExpanded,
  onUpdate,
  onDelete
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);

  useEffect(() => {
    if (defaultExpanded) setExpanded(true);
  }, [defaultExpanded]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const effectiveType = inferStepType(step as Record<string, unknown>);
  const description = (step as { description?: string }).description;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        mb: 1,
        '&:hover': { borderColor: 'primary.main' }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          px: 1,
          py: 0.5,
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(prev => !prev)}
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            mr: 0.5,
            mt: 0.25
          }}
          onClick={e => e.stopPropagation()}
        >
          <DragIndicatorIcon fontSize="small" color="action" />
        </Box>
        <Chip
          label={step.index + 1}
          size="small"
          sx={{ minWidth: 28, mr: 1, mt: 0.25, height: 22, fontSize: '0.7rem' }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: 1,
            mt: 0.25,
            color: 'primary.main'
          }}
        >
          {STEP_ICONS[effectiveType] ?? null}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {STEP_TYPE_LABELS[effectiveType] ?? effectiveType}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1, minWidth: 0 }}
              noWrap
            >
              {resolveNominalToken(getStepSummary(step), testPointRef ?? null)}
            </Typography>
          </Box>
          {description && (
            <Tooltip title={description} placement="bottom-start" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  fontStyle: 'italic',
                  lineHeight: 1.3,
                  mt: 0.25
                }}
              >
                {description}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <IconButton
          size="small"
          color="error"
          onClick={e => {
            e.stopPropagation();
            onDelete(step.id);
          }}
          sx={{ mt: 0.25 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        <Box sx={{ mt: 0.5 }}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          <StepForm
            step={step}
            testPointRef={testPointRef}
            onUpdate={onUpdate}
          />
        </CardContent>
      </Collapse>
    </Card>
  );
};
